import { CSSProperties } from 'react';

/**
 * FlowProgress — how far the traveler is through Alma's onboarding.
 *
 *   FlowProgressCount → serif fraction for ChatHeader's `actions` slot:
 *                       "3/5" in Phase 1, "Part 2/2" in Phase 2.
 *   FlowProgressBar   → hairline under the header: one segment per question
 *                       in Phase 1, a single filling bar in Phase 2.
 *
 * Callers own the "never goes backwards" rule; these just draw what they get.
 */
export interface FlowProgressCountProps {
  current: number;
  total: number;
  /** Text before the number, e.g. "Part " for Phase 2. */
  prefix?: string;
  /** Spoken label, e.g. "Question 3 of 5". */
  ariaLabel: string;
  style?: CSSProperties;
}

export function FlowProgressCount({ current, total, prefix = '', ariaLabel, style }: FlowProgressCountProps) {
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      style={{
        fontFamily: 'var(--font-serif)',
        fontSize: 19,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span style={{ color: 'var(--text)' }}>{prefix}{current}</span>
      <span style={{ color: 'var(--text-dim)' }}>/{total}</span>
    </span>
  );
}

export type FlowProgressBarProps =
  | {
      /** Phase 1: one segment per question; done and current segments are filled. */
      variant: 'segments';
      current: number;
      total: number;
      style?: CSSProperties;
    }
  | {
      /** Phase 2: one bar filling as Alma asks her questions. */
      variant: 'fill';
      value: number;
      max: number;
      /** Spoken label, e.g. "Alma's questions". */
      label: string;
      style?: CSSProperties;
    };

export function FlowProgressBar(props: FlowProgressBarProps) {
  if (props.variant === 'segments') {
    const { current, total, style } = props;
    return (
      <div aria-hidden="true" style={{ display: 'flex', gap: 4, ...style }}>
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            style={{
              flex: 1,
              height: 2,
              borderRadius: 1,
              background: i < current ? 'var(--text)' : 'var(--line)',
              transition: 'background-color .2s',
            }}
          />
        ))}
      </div>
    );
  }

  const { value, max, label, style } = props;
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.min(value, max)}
      style={{ height: 2, borderRadius: 1, background: 'var(--line)', overflow: 'hidden', ...style }}
    >
      <div className="sk-flow-fill" style={{ width: `${pct}%`, height: '100%', background: 'var(--text)' }} />
    </div>
  );
}
